import { useState, useEffect, useCallback } from 'react';
import { Search, X } from 'lucide-react';
import { useCollection } from '../hooks/useCollection';
import { RARITY_BUCKETS } from '../utils/constants';

const OWNERSHIP_PILLS = [
  { key: 'all', label: 'All' },
  { key: 'owned', label: 'Collected' },
  { key: 'not-owned', label: 'Missing' },
];

const Pill = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-3 py-1.5 rounded-full text-[12px] font-medium whitespace-nowrap transition-colors flex-shrink-0 ${
      active ? 'bg-navy-dark text-white' : 'bg-cream-bg text-ink-secondary hover:bg-border'
    }`}
  >
    {children}
  </button>
);

const FilterBar = () => {
  const {
    filters,
    settings,
    setFilters,
    setSettings,
    getFilterOptions,
    cards,
    getFilteredCards
  } = useCollection();

  const [searchInput, setSearchInput] = useState('');

  const filterOptions = getFilterOptions();
  const filteredCards = getFilteredCards();

  useEffect(() => {
    if (filters.search !== searchInput) {
      setSearchInput(filters.search || '');
    }
  }, [filters.search]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilters({ search: searchInput });
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchInput, setFilters]);

  const handleSearchChange = useCallback((e) => {
    setSearchInput(e.target.value);
  }, []);

  const handleRarityChange = useCallback((rarity) => {
    setFilters({ rarity });
  }, [setFilters]);

  const handleTypeChange = useCallback((type) => {
    setFilters({ type });
  }, [setFilters]);

  const handleOwnershipChange = useCallback((ownership) => {
    setFilters({ ownership });
  }, [setFilters]);

  const handleArtistChange = useCallback((artist) => {
    setFilters({ artist });
  }, [setFilters]);

  const handleCompletionViewChange = useCallback((view) => {
    setSettings({ completionView: view });
  }, [setSettings]);

  const clearAllFilters = useCallback(() => {
    setSearchInput('');
    setFilters({
      search: '',
      rarity: 'all',
      type: 'all',
      ownership: 'all',
      artist: 'all'
    });
  }, [setFilters]);

  const hasActiveFilters = searchInput !== '' ||
    filters.rarity !== 'all' ||
    filters.type !== 'all' ||
    filters.ownership !== 'all' ||
    filters.artist !== 'all';

  return (
    <div className="bg-white border-b border-border px-4 md:px-10 py-3.5">
      <div className="flex items-center gap-2 mb-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint pointer-events-none" />
          <input
            type="text"
            placeholder="Search by name or number..."
            value={searchInput}
            onChange={handleSearchChange}
            className="w-full pl-8 pr-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-dark/20"
            autoComplete="off"
            spellCheck="false"
          />
        </div>

        <select
          value={filters.type}
          onChange={(e) => handleTypeChange(e.target.value)}
          className="px-2.5 py-2 border border-border rounded-lg text-[12px] text-ink-secondary bg-white flex-shrink-0"
        >
          <option value="all">All Types</option>
          {filterOptions.types.map((type) => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>

        <select
          value={filters.artist}
          onChange={(e) => handleArtistChange(e.target.value)}
          className="hidden sm:block px-2.5 py-2 border border-border rounded-lg text-[12px] text-ink-secondary bg-white max-w-[140px] flex-shrink-0"
        >
          <option value="all">All Artists</option>
          {filterOptions.artists.map((artist) => (
            <option key={artist} value={artist}>{artist}</option>
          ))}
        </select>

        {hasActiveFilters && (
          <button
            onClick={clearAllFilters}
            className="text-ink-faint hover:text-brick p-2 flex-shrink-0"
            title="Clear filters"
          >
            <X size={16} />
          </button>
        )}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1.5 overflow-x-auto">
          {OWNERSHIP_PILLS.map((p) => (
            <Pill key={p.key} active={filters.ownership === p.key} onClick={() => handleOwnershipChange(p.key)}>
              {p.label}
            </Pill>
          ))}
          <span className="w-px h-4 bg-border mx-1 flex-shrink-0" />
          <Pill active={filters.rarity === 'all'} onClick={() => handleRarityChange('all')}>
            All Rarities
          </Pill>
          {RARITY_BUCKETS.map((bucket) => (
            <Pill key={bucket.key} active={filters.rarity === bucket.key} onClick={() => handleRarityChange(bucket.key)}>
              {bucket.label}
            </Pill>
          ))}
        </div>

        <div className="flex items-center bg-cream-bg rounded-full p-1 ml-auto flex-shrink-0">
          <button
            onClick={() => handleCompletionViewChange('base')}
            className={`px-3 py-1 rounded-full text-[12px] font-medium transition-colors ${
              settings.completionView === 'base' ? 'bg-navy-dark text-white' : 'text-ink-secondary'
            }`}
          >
            Base Set
          </button>
          <button
            onClick={() => handleCompletionViewChange('master')}
            className={`px-3 py-1 rounded-full text-[12px] font-medium transition-colors ${
              settings.completionView === 'master' ? 'bg-navy-dark text-white' : 'text-ink-secondary'
            }`}
          >
            Master Set
          </button>
        </div>
      </div>

      {hasActiveFilters && (
        <div className="mt-2.5 text-[12px] text-ink-secondary">
          Showing {filteredCards.length} of {cards.length} cards
        </div>
      )}
    </div>
  );
};

export default FilterBar;
